<script setup>
import { ref, watch, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useBotTestChat } from 'dashboard/composables/useBotTestChat';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  bot: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['conversationChanged']);

const { t } = useI18n();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const {
  messages,
  conversation,
  loading,
  sending,
  hasMessages,
  noInboxConnected,
  errorMessage,
  messagesVersion,
  initChat,
  sendMessage,
  resetChat,
  changeBotId,
  refresh,
} = useBotTestChat();

watch(
  () => conversation.value,
  newConv => {
    emit('conversationChanged', newConv);
  }
);

// Navigate to inbox settings to configure bot
const goToInboxSettings = () => {
  router.push({
    name: 'settings_inbox_list',
    params: { accountId: accountId.value },
  });
};

// Local state
const inputMessage = ref('');
const messagesContainer = ref(null);
const showClearConfirm = ref(false);

// Watch for bot changes
watch(
  () => props.bot?.id,
  async newBotId => {
    if (newBotId) {
      await changeBotId(newBotId);
    }
  },
  { immediate: true }
);

// Auto-scroll to bottom when messages change (using version tracker for reliability)
watch(
  [() => messages.value.length, () => messagesVersion.value],
  () => {
    // Use multiple nextTick to ensure DOM is fully updated
    nextTick(() => {
      nextTick(() => {
        scrollToBottom();
      });
    });
  },
  { flush: 'post' }
);

const scrollToBottom = (smooth = false) => {
  if (!messagesContainer.value) return;

  // Use requestAnimationFrame for smoother scrolling
  requestAnimationFrame(() => {
    if (!messagesContainer.value) return;

    if (smooth) {
      messagesContainer.value.scrollTo({
        top: messagesContainer.value.scrollHeight,
        behavior: 'smooth',
      });
    } else {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const handleSend = async () => {
  const content = inputMessage.value.trim();
  if (!content || sending.value) return;

  inputMessage.value = '';
  await sendMessage(content);
};

const handleKeyDown = event => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
};

const handleReset = async () => {
  await resetChat(false);
};

const handleClearAndReset = async () => {
  showClearConfirm.value = false;
  await resetChat(true);
};

const formatTime = dateString => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isUserMessage = message => {
  return message.message_type === 'incoming';
};

const isPrivateMessage = message => {
  return message.private === true;
};

// Manual refresh
const handleRefresh = async () => {
  await refresh();
  scrollToBottom(true);
};
</script>

<template>
  <div
    class="flex flex-col h-full bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 overflow-hidden"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-slate-75 dark:border-slate-800 bg-slate-50 dark:bg-slate-800"
    >
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-message-circle text-woot-500 dark:text-woot-400 w-5 h-5"
        />
        <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {{ t('OMNI.TEST_CHAT.TITLE') }}
        </h3>
      </div>

      <div class="flex items-center gap-1">
        <!-- Refresh button -->
        <button
          class="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
          :title="t('OMNI.TEST_CHAT.REFRESH')"
          @click="handleRefresh"
        >
          <span class="i-lucide-refresh-cw w-4 h-4" />
        </button>

        <!-- Reset button -->
        <button
          class="p-1.5 text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
          :title="t('OMNI.TEST_CHAT.RESET')"
          :disabled="sending"
          @click="handleReset"
        >
          <span class="i-lucide-rotate-ccw w-4 h-4" />
        </button>

        <!-- Clear history button -->
        <button
          class="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
          :title="t('OMNI.TEST_CHAT.CLEAR_HISTORY')"
          :disabled="sending"
          @click="showClearConfirm = true"
        >
          <span class="i-lucide-trash-2 w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Bot info -->
    <div
      v-if="bot"
      class="px-4 py-2 border-b border-slate-75 dark:border-slate-800 bg-slate-25 dark:bg-slate-850"
    >
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-bot w-4 h-4 text-slate-400 dark:text-slate-500"
        />
        <span class="text-xs text-slate-600 dark:text-slate-400">
          {{ bot.name }}
        </span>
      </div>
    </div>

    <!-- Messages area -->
    <div
      ref="messagesContainer"
      class="flex-1 overflow-y-auto p-4 space-y-3 min-h-0"
    >
      <!-- Loading -->
      <div
        v-if="loading"
        class="flex items-center justify-center h-full"
      >
        <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-woot-500" />
      </div>

      <!-- No bot selected -->
      <div
        v-else-if="!bot"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <span
          class="i-lucide-message-circle-off text-4xl text-slate-300 dark:text-slate-600 mb-2"
        />
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('OMNI.TEST_CHAT.SELECT_BOT') }}
        </p>
      </div>

      <!-- Bot not connected to inbox -->
      <div
        v-else-if="noInboxConnected"
        class="flex flex-col items-center justify-center h-full text-center px-4"
      >
        <span
          class="i-lucide-unplug text-4xl text-amber-500 dark:text-amber-400 mb-3"
        />
        <p class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {{ t('OMNI.TEST_CHAT.NO_INBOX_CONNECTED') }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-4">
          {{ errorMessage || t('OMNI.TEST_CHAT.NO_INBOX_HINT') }}
        </p>
        <button
          v-if="isAdmin"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 transition-colors"
          @click="goToInboxSettings"
        >
          <span class="i-lucide-settings w-4 h-4" />
          {{ t('OMNI.TEST_CHAT.CONFIGURE_INBOX') }}
        </button>
        <p
          v-else
          class="text-xs text-slate-400 dark:text-slate-500 italic"
        >
          {{ t('OMNI.TEST_CHAT.CONTACT_ADMIN') }}
        </p>
      </div>

      <!-- No messages -->
      <div
        v-else-if="!hasMessages"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <span
          class="i-lucide-message-square text-4xl text-slate-300 dark:text-slate-600 mb-2"
        />
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-1">
          {{ t('OMNI.TEST_CHAT.EMPTY_STATE') }}
        </p>
        <p class="text-xs text-slate-400 dark:text-slate-500">
          {{ t('OMNI.TEST_CHAT.EMPTY_HINT') }}
        </p>
      </div>

      <!-- Messages list -->
      <template v-else>
        <div
          v-for="message in messages"
          :key="message.id"
          class="flex"
          :class="isUserMessage(message) ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] rounded-lg px-3 py-2"
            :class="[
              isPrivateMessage(message)
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 border border-amber-300 dark:border-amber-700 rounded-bl-sm'
                : isUserMessage(message)
                  ? 'bg-woot-500 text-white rounded-br-sm'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-sm'
            ]"
          >
            <!-- Private indicator -->
            <div
              v-if="isPrivateMessage(message)"
              class="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mb-1"
            >
              <span class="i-lucide-lock w-3 h-3" />
              <span>{{ t('OMNI.TEST_CHAT.PRIVATE_NOTE') }}</span>
            </div>

            <!-- Sender name -->
            <div
              class="text-xs mb-0.5"
              :class="
                isPrivateMessage(message)
                  ? 'text-amber-700 dark:text-amber-300'
                  : isUserMessage(message)
                    ? 'text-woot-100'
                    : 'text-slate-500 dark:text-slate-400'
              "
            >
              {{ message.sender_name }}
            </div>

            <!-- Content -->
            <p
              class="text-sm whitespace-pre-wrap break-words"
              :class="{ 'opacity-60': message.pending }"
            >
              {{ message.content }}
            </p>

            <!-- Time -->
            <div
              class="text-xs mt-1 text-right"
              :class="
                isPrivateMessage(message)
                  ? 'text-amber-600 dark:text-amber-400'
                  : isUserMessage(message)
                    ? 'text-woot-200'
                    : 'text-slate-400 dark:text-slate-500'
              "
            >
              <span v-if="message.pending" class="i-lucide-clock w-3 h-3 inline mr-1" />
              {{ formatTime(message.created_at) }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Input area -->
    <div
      class="border-t border-slate-75 dark:border-slate-800 p-3 bg-slate-50 dark:bg-slate-800"
    >
      <div class="flex items-end gap-2">
        <textarea
          v-model="inputMessage"
          class="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
          :placeholder="noInboxConnected ? t('OMNI.TEST_CHAT.INPUT_DISABLED') : t('OMNI.TEST_CHAT.PLACEHOLDER')"
          rows="1"
          :disabled="!bot || sending || noInboxConnected"
          @keydown="handleKeyDown"
        />

        <button
          class="p-2 bg-woot-500 text-white rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="!bot || !inputMessage.trim() || sending || noInboxConnected"
          @click="handleSend"
        >
          <span
            v-if="sending"
            class="i-lucide-loader-2 w-5 h-5 animate-spin"
          />
          <span v-else class="i-lucide-send w-5 h-5" />
        </button>
      </div>

      <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
        {{ noInboxConnected ? '' : t('OMNI.TEST_CHAT.ENTER_HINT') }}
      </p>
    </div>

    <!-- Clear confirm modal -->
    <div
      v-if="showClearConfirm"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="showClearConfirm = false"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-sm mx-4 shadow-xl"
      >
        <h4 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {{ t('OMNI.TEST_CHAT.CONFIRM_CLEAR_TITLE') }}
        </h4>
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
          {{ t('OMNI.TEST_CHAT.CONFIRM_CLEAR_MESSAGE') }}
        </p>
        <div class="flex gap-2 justify-end">
          <button
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
            @click="showClearConfirm = false"
          >
            {{ t('OMNI.TEST_CHAT.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
            @click="handleClearAndReset"
          >
            {{ t('OMNI.TEST_CHAT.CLEAR_AND_RESET') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

